



import React, { Fragment, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import ModalVideo from "react-modal-video";
import { Helmet } from "react-helmet-async";
import Header from "../../components/header/Header";
import PageTitle from "../../components/pagetitle/PageTitle";
import Scrollbar from "../../components/scrollbar/scrollbar";
import Footer from "../../components/footer/Footer";
import CtaSection from "../../components/CtaSection/CtaSection";
import ProcessTechnology from "../../components/software-company-components/ProcessTechnology/ProcessTechnology";
import icon from "../../images/icons/icon_check_3.svg";
import Services from "../../api/service";
import { useSeo } from "../../hooks/useSeo";
import ProjectSection from "../../components/ProjectSection/ProjectSection";
import ServiceProjectSection from "../../components/ProjectSection/Service_Project_Section";
import ServiceOutcome from "./ServiceOutcome";
import FaqSection from "./FaqSection";
import WhyChooseUs from "./WhyChooseUs";

const staticMeta = {
  title: "Service Details | RashTech Info",
  description:
    "Detailed view of our service offering at RashTech Info, including outcomes, capabilities, and approach.",
  url: "https://rashtechinfo.com/service",
  image: "https://rashtechinfo.com/og-image.png",
  siteName: "RashTech Info",
  keywords: "service, offering, outcomes, IT services, RashTech Info",
  themeColor: "#0A0D1C",
};

// Define meta data mapping based on slugs
const slugMetaData = {
  "web-development-services": {
    title: "Web Development Company in Alwar & India | RashTech Info",
    description: "RashTech Info provides web development services in Alwar and across India. Get responsive, SEO-friendly, and custom websites to grow your business."
  },
  "app-development-services": {
    title: "Mobile App Development Company in Alwar & India | RashTech Info",
    description: "Build Android & iOS apps with RashTech Info. Trusted mobile app development company in Alwar and India delivering scalable and secure solutions."
  },
  "e-commerce-solutions-services": {
    title: "E-commerce Solutions Company in Alwar & India | RashTech Info",
    description: "RashTech Info provides complete e-commerce solutions in Alwar and across India. Build your online store with secure payment gateways and responsive design."
  },
  "custom-software-services": {
    title: "Custom Software Development in Alwar & India | RashTech Info",
    description: "RashTech Info builds tailored software solutions in Alwar and India. Scalable, secure, and reliable custom software development for all businesses."
  },
  "ui-ux-development-services": {
    title: "UI/UX Design Services in Alwar & India | RashTech Info",
    description: "RashTech Info offers UI/UX design services in Alwar and across India. Modern, responsive, and user-friendly designs for web and mobile apps."
  },
  "graphic-design-services": {
    title: "Graphic Design Services in Alwar & India | RashTech Info",
    description: "Build your brand identity with RashTech Info's graphic design services in Alwar and across India. Creative logos, branding, and marketing designs."
  },
  "video-editing-services": {
    title: "Video Editing Services in Alwar & India | RashTech Info",
    description: "Create engaging videos with RashTech Info's video editing services in Alwar and across India. High-quality, creative, and custom video solutions."
  },
  "seo-services": {
    title: "SEO Services in Alwar & India | SEO Company - RashTech Info",
    description: "Rank higher on Google with RashTech Info's SEO services in Alwar and India. Boost traffic, visibility, and leads with expert SEO strategies."
  },
  "smo-services": {
    title: "SMO Services in Alwar & India | Social Media Marketing - RashTech Info",
    description: "Grow your brand with RashTech Info's SMO services in Alwar and across India. Boost engagement and presence on Facebook, Instagram, and more."
  }
};

const ServiceSinglePage = () => {
  const { slug } = useParams();
  // console.log(slug);

  const ServiceDetails = Services.find((item) => item.slug === slug);
  console.log(ServiceDetails);
  const [isOpen, setOpen] = useState(false);

  const { seo } = useSeo("/getSeo");

  const computed = useMemo(() => {
    // Check if we have specific meta data for this slug
    const slugSpecificMeta = slugMetaData[slug] || {};
    
    const dynamic = {
      title: seo?.title,
      description: seo?.metaDescription,
      keywords: seo?.metaKeywords,
      canonicalUrl: seo?.canonicalUrl,
    };

    // Use slug-specific meta data if available, otherwise fall back to other sources
    const pageTitle = slugSpecificMeta.title || 
      (ServiceDetails ? `${ServiceDetails.title} | Services | RashTech Info` : "") ||
      dynamic.title || 
      staticMeta.title;

    const description = slugSpecificMeta.description ||
      ServiceDetails?.description ||
      dynamic.description ||
      staticMeta.description;

    return {
      title: pageTitle,
      description,
      keywords: dynamic.keywords || staticMeta.keywords,
      url:
        dynamic.canonicalUrl ||
        (slug ? `https://rashtechinfo.com/${slug}` : staticMeta.url),
      image: staticMeta.image,
      siteName: staticMeta.siteName,
      themeColor: staticMeta.themeColor,
    };
  }, [seo, ServiceDetails, slug]);

  return (
    <Fragment>
      <Helmet>
        <title>{computed.title}</title>
        <meta name="description" content={computed.description} />
        {computed.keywords && (
          <meta name="keywords" content={computed.keywords} />
        )}
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="theme-color" content={computed.themeColor} />
        {computed.url && <link rel="canonical" href={computed.url} />}
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content={computed.siteName} />
        <meta property="og:title" content={computed.title} />
        <meta property="og:description" content={computed.description} />
        {computed.url && <meta property="og:url" content={computed.url} />}
        {computed.image && (
          <meta property="og:image" content={computed.image} />
        )}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={computed.title} />
        <meta name="twitter:description" content={computed.description} />
        {computed.image && (
          <meta name="twitter:image" content={computed.image} />
        )}
      </Helmet>

      <Header />
      <main className="page_content service-single-page">
        <PageTitle
          pageTitle={ServiceDetails.title}
          pagesub={"Details"}
          pageTop={"Services"}
        />

        <section className="service_details_section section_space bg-light">
          <div className="container">
            <div className="details_item_image position-relative">
              <img src={ServiceDetails.sImg} alt="Service Details" />
              <button
                className="video_btn ripple_effect"
                onClick={() => setOpen(true)}
              ></button>
            </div>

            {ServiceDetails.bold && (
              <h4 className="details_item_subheading">{ServiceDetails.bold}</h4>
            )}

            <p>{ServiceDetails.description}</p>

            {ServiceDetails.description2 && (
              <p>{ServiceDetails.description2}</p>
            )}

            <ProcessTechnology />
            <WhyChooseUs slug={slug} />

            <ServiceOutcome />
          </div>

          <ServiceProjectSection />
          <FaqSection />

        </section>
      </main>

      <CtaSection />
      <Footer />
      <Scrollbar />
      <ModalVideo
        channel="youtube"
        autoplay
        isOpen={isOpen}
        videoId="eWQy5gOt9Rc"
        onClose={() => setOpen(false)}
      />
    </Fragment>
  );
};
export default ServiceSinglePage;